import { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { money, modoTraslado, setModoTraslado } from '../events/util';
import Toggle from '../events/Toggle';

const INK = '#9A9A9A';

export default function GruposLotes() {
  const api = useApi();
  const [groups, setGroups] = useState([]);
  const [newName, setNewName] = useState('');

  const load = () => api.get('/api/lot-groups').then(setGroups).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line

  async function createGroup() {
    if (!newName.trim()) return;
    try { await api.post('/api/lot-groups', { name: newName }); setNewName(''); load(); }
    catch (e) { alert(e.message); }
  }

  return (
    <div className="lb-fade">
      <div className="lb-display" style={{ fontSize: 24 }}>Grupos de lotes</div>
      <p style={{ color: INK, fontSize: 14, marginTop: 4, marginBottom: 22 }}>Armá plantillas de lotes una vez y reutilizalas al crear eventos. Al aplicar un grupo, los lotes se crean pausados: activá los que correspondan a esa noche.</p>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, maxWidth: 460 }}>
        <input className="lb-input" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Nombre del grupo (ej: tu boliche, otro salón)" onKeyDown={e => e.key === 'Enter' && createGroup()} />
        <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={createGroup}>+ Crear grupo</button>
      </div>

      <div style={{ border: '1px solid rgba(236,38,143,0.3)', background: 'rgba(236,38,143,0.06)', padding: '12px 16px', borderRadius: 6, marginBottom: 24, maxWidth: 640, fontSize: 12.5, color: '#cfcfd6', lineHeight: 1.5 }}>
        <b style={{ color: '#FF9DCD' }}>Importante:</b> al aplicar un grupo a un evento, sus lotes se crean <b>pausados</b> por defecto. Andá a <b>Administración del evento → Productos</b> y activá solo los que correspondan a esa noche.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 20 }} className="tk-2col">
        {groups.map(g => <GroupCard key={g.id} group={g} api={api} reload={load} />)}
        {groups.length === 0 && <div style={{ color: INK, fontSize: 13 }}>Todavía no hay grupos. Creá el primero.</div>}
      </div>
    </div>
  );
}

function GroupCard({ group, api, reload }) {
  // form = null (cerrado) | { item: null } (crear) | { item } (editar)
  const [form, setForm] = useState(null);

  async function rmItem(id) { try { await api.del(`/api/lot-groups/items/${id}`); reload(); } catch (e) { alert(e.message); } }
  async function delGroup() {
    if (!window.confirm(`¿Eliminar el grupo ${group.name}?`)) return;
    try { await api.del(`/api/lot-groups/${group.id}`); reload(); } catch (e) { alert(e.message); }
  }

  return (
    <div className="lb-card" style={{ padding: '20px 22px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div className="lb-h" style={{ fontSize: 17 }}>{group.name}</div>
          <div style={{ fontSize: 11, color: INK, marginTop: 2 }}>{group.items.length} lotes</div>
        </div>
        <button className="lb-iconbtn" onClick={delGroup} title="Eliminar grupo">✕</button>
      </div>

      <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {group.items.map(it => {
          const ch = Array.isArray(it.channels) ? it.channels : [];
          return (
            <div key={it.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto auto', gap: 8, alignItems: 'center', padding: '8px 0', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <div>
                <div style={{ color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-display)', textTransform: 'uppercase' }}>{it.name}</div>
                <div style={{ fontSize: 11, color: INK }}>{[ch.includes('rrpp') && 'Tradicional', ch.includes('online') && 'Online', ch.includes('boleteria') && 'Boletería'].filter(Boolean).join(' · ') || 'Sin canal'}{it.solo_traslado ? ' · Solo trasl.' : it.has_transport ? ' · Traslado' : ''}{it.roles === 'owners' ? ' · Solo dueños' : ''}</div>
              </div>
              <span style={{ color: 'var(--ink-800)', fontFamily: 'var(--font-display)', fontSize: 13 }}>{money(it.price)}</span>
              <span style={{ color: INK, fontSize: 12 }}>cupo {it.max_capacity > 0 ? it.max_capacity : 'ilimitado'}</span>
              <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setForm({ item: it })}>Editar</button>
              <button className="lb-iconbtn" onClick={() => rmItem(it.id)}>✕</button>
            </div>
          );
        })}
        {group.items.length === 0 && <div style={{ fontSize: 12, color: INK, padding: '6px 0' }}>Sin lotes. Agregá el primero.</div>}
      </div>

      <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed rgba(255,255,255,0.16)' }}>
        <button className="lb-btn lb-btn--white lb-btn--sm" onClick={() => setForm({ item: null })}>+ Agregar lote</button>
      </div>

      {form && <ItemModal group={group} item={form.item} api={api} onClose={() => setForm(null)} onSaved={() => { setForm(null); reload(); }} />}
    </div>
  );
}

// Form completo del lote del grupo: mismos campos que el lote del evento, menos el Estado.
function ItemModal({ group, item, api, onClose, onSaved }) {
  const creating = !item;
  const ch = item && Array.isArray(item.channels) ? item.channels : ['online', 'rrpp'];
  const [f, setF] = useState({
    name: item ? item.name : '',
    price: item ? String(item.price) : '',
    max_capacity: item ? String(item.max_capacity) : '',
    tradicional: ch.includes('rrpp'),
    online: ch.includes('online'),
    boleteria: ch.includes('boleteria'),
    roles: item ? (item.roles || 'all') : 'all',
    has_transport: item ? !!item.has_transport : false,
    solo_traslado: item ? !!item.solo_traslado : false,
    is_birthday: item ? !!item.is_birthday : false,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  // Boletería es excluyente: se vende SOLO en la caja de boletería. Tradicional y
  // online conviven entre sí, pero al activar boletería se apagan (y viceversa).
  // Misma regla que el formulario de lotes del evento: boletería convive con ONLINE
  // (descomprime la puerta) pero no con RRPP. Si acá quedara la regla vieja, un grupo
  // guardado con los dos canales se crearía mal en cada evento que lo use.
  const toggleTrad = () => setF(s => s.tradicional ? { ...s, tradicional: false } : { ...s, tradicional: true, boleteria: false });
  const toggleOnline = () => setF(s => ({ ...s, online: !s.online }));
  const toggleBoleteria = () => setF(s => s.boleteria ? { ...s, boleteria: false } : { ...s, boleteria: true, tradicional: false });
  const valid = f.name.trim() && f.price !== '';

  async function save() {
    if (!valid) return;
    setSaving(true);
    const channels = [];
    if (f.tradicional) channels.push('rrpp');
    if (f.online) channels.push('online');
    if (f.boleteria) channels.push('boleteria');
    const body = { name: f.name, price: +f.price || 0, max_capacity: +f.max_capacity || 0, channels, roles: f.roles, has_transport: f.has_transport, solo_traslado: f.solo_traslado, is_birthday: f.boleteria ? f.is_birthday : false };
    try {
      if (creating) await api.post(`/api/lot-groups/${group.id}/items`, body);
      else await api.patch(`/api/lot-groups/items/${item.id}`, body);
      onSaved();
    } catch (e) { alert(e.message); } finally { setSaving(false); }
  }
  async function del() {
    if (!item) return onClose();
    if (!window.confirm('¿Eliminar este lote del grupo?')) return;
    try { await api.del(`/api/lot-groups/items/${item.id}`); onSaved(); } catch (e) { alert(e.message); }
  }

  return (
    <div className="lb-modal-backdrop" onClick={onClose}>
      <div className="lb-modal" onClick={e => e.stopPropagation()}>
        <div style={{ padding: '24px 28px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div className="lb-eyebrow">{creating ? 'Nuevo lote del grupo' : 'Editar lote del grupo'}</div>
            <div className="lb-h" style={{ fontSize: 20, marginTop: 4 }}>{f.name || 'SIN NOMBRE'}</div>
          </div>
          <button className="lb-iconbtn" onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Field label="Nombre del lote"><input className="lb-input" value={f.name} onChange={e => set('name', e.target.value)} placeholder="LOTE 1" /></Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="Precio" hint="$0 para invitación"><input className="lb-input" value={f.price} inputMode="numeric" onChange={e => set('price', e.target.value.replace(/\D/g, ''))} placeholder="0" /></Field>
            <Field label="Cupo máximo" hint="Vacío = ilimitado"><input className="lb-input" value={f.max_capacity} inputMode="numeric" onChange={e => set('max_capacity', e.target.value.replace(/\D/g, ''))} placeholder="Ilimitado" /></Field>
          </div>

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 18 }}>
            <div className="lb-label" style={{ marginBottom: 12 }}>Medio de venta</div>
            <Row title="Tradicional" sub="Venta por RRPP / generación manual de QRs"><Toggle on={f.tradicional} onClick={toggleTrad} label={f.tradicional ? 'Sí' : 'No'} /></Row>
            <Row title="Online" sub="Compra desde la web pública"><Toggle on={f.online} onClick={toggleOnline} label={f.online ? 'Sí' : 'No'} /></Row>
            <Row title="Boletería" sub="Se vende en la caja de la puerta. Se puede combinar con Online (ayuda a descomprimir la boletería), pero no con RRPP."><Toggle on={f.boleteria} onClick={toggleBoleteria} label={f.boleteria ? 'Sí' : 'No'} /></Row>
            {f.boleteria && <Row title="Lista de cumpleaños" sub="Este es el lote gratis de cumpleañeros: en boletería se elige la persona de la lista y se emite su entrada"><Toggle on={f.is_birthday} onClick={() => set('is_birthday', !f.is_birthday)} label={f.is_birthday ? 'Sí' : 'No'} /></Row>}
            <Row title="Traslado" sub="Combo = entrada + traslado (dos QR). Solo Trasl. = se vende el traslado sin entrada; no cuenta como persona y no va en boletería">
              <div style={{ display: 'flex', gap: 6 }}>
                {[['no', 'Sin traslado'], ['combo', 'Combo'], ['solo', 'Solo Trasl.']].map(([k, t]) => (
                  <button key={k} type="button" className={'lb-btn lb-btn--sm ' + (modoTraslado(f) === k ? 'lb-btn--white' : 'lb-btn--ghost')}
                    onClick={() => setModoTraslado(set, k)}>{t}</button>
                ))}
              </div>
            </Row>
          </div>

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 18 }}>
            <div className="lb-label" style={{ marginBottom: 10 }}>Quién puede generarlo</div>
            <div style={{ display: 'flex', gap: 10 }}>
              {[['all', 'Dueños y vendedores'], ['owners', 'Solo dueños']].map(([v, lbl]) => (
                <button key={v} onClick={() => set('roles', v)} style={{ flex: 1, cursor: 'pointer', padding: '12px 14px', textAlign: 'left', background: f.roles === v ? 'rgba(255,255,255,0.05)' : 'var(--ink-050)', border: '1px solid ' + (f.roles === v ? 'rgba(255,255,255,0.5)' : 'var(--border)'), color: f.roles === v ? '#fff' : 'var(--ink-700)', fontWeight: 600, fontSize: 12.5, borderRadius: 4 }}>{lbl}</button>
              ))}
            </div>
          </div>

          <div style={{ fontSize: 11, color: INK }}>Los cambios aplican al grupo y se usan en los próximos eventos donde lo apliques. No modifican lotes ya creados en eventos. Al aplicar el grupo, cada lote se crea <b style={{ color: '#cfcfd6' }}>pausado</b>: activá los que necesites en Productos del evento.</div>
        </div>

        <div style={{ padding: '18px 28px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: 12, alignItems: 'center' }}>
          {!creating && <button className="lb-iconbtn" onClick={del} style={{ width: 'auto', padding: '0 12px', height: 38 }}>Eliminar</button>}
          <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={onClose} style={{ marginLeft: 'auto' }}>Cancelar</button>
          <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={save} disabled={!valid || saving}>{saving ? 'Guardando…' : (creating ? 'Agregar' : 'Guardar')}</button>
        </div>
      </div>
    </div>
  );
}

function Row({ title, sub, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
      <div><div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{title}</div><div style={{ fontSize: 11, color: 'var(--ink-600)' }}>{sub}</div></div>
      {children}
    </div>
  );
}

function Field({ label, hint, children }) { return <div className="lb-field"><div className="lb-label">{label}</div>{children}{hint && <div className="lb-hint">{hint}</div>}</div>; }
